import config from '@iotv/config';
import { isDeviceConfig } from '@iotv/datamodel-core';
import { getZombieInstanceFromPrimaryKey } from '@iotv/iotv-v3-types';
import { useContext, useEffect, useRef } from 'react';
import { AppMainViewContext } from '../../../../../ContextProviders/AppContextProvider';
import { NodeEdgeMapperContext } from '../../../../../ContextProviders/NodeEdgeMapperComponentContext';
import { subscribeToMQTTChannel } from '../../../../../data/daoFunctions/daoFunctions';
import { isDeviceEnabledType } from '../../../../../data/TypeHelpers';
import { isValidDCT } from '../Entity/Reducers/EnitityControlReducer';
import { EntityGroupControlContext } from '../EntityGroup/Reducers/EntityGroupControlReducer';
const debug = process.env.REACT_APP_DEBUG && false;
/**
 * common hook, with dependency that calling component is a consumer of
 * NodeEdgeMapperContext for checking that the message comes from an in scope entity
 * EntityGroupControlContext for dispatching an update to device config
 */
export const useDeviceControlChannelActions = ({ device, canaryString, loadDeviceConfigs }) => {
    const { dispatch } = useContext(EntityGroupControlContext);
    const [{ contextUser }] = useContext(AppMainViewContext);
    const { state: nodeEdgeMapperState, dispatch: nodeEdgeMapperDispatch } = useContext(NodeEdgeMapperContext);
    const dctChannelSubRef = useRef(undefined);
    const responseChannelSubRef = useRef(undefined);
    const { nemHash } = nodeEdgeMapperState;
    useEffect(() => {
        const getMatchedEntityViaZombieDevice = (deviceZombie) => {
            const device = nodeEdgeMapperState.nodeEdgeMapper.getItem(deviceZombie);
            let entity = null;
            if (device) {
                const entities = nodeEdgeMapperState.nodeEdgeMapper.getParentNodes(device).filter(isDeviceEnabledType);
                if (entities.length === 1) {
                    entity = entities.at(0);
                }
                if (entities.length > 1) {
                    console.log(`WARN: isMatchedByDevice returned more than one entity for a device`, { device, entities });
                }
            }
            return entity;
        };
        const isDCTMatchedByDeviceEui = (receivedObject) => {
            const stateEntities = nodeEdgeMapperState.nodeEdgeMapper.getContainedItems().filter(isDeviceEnabledType);
            const entity = stateEntities.find((item) => item.deviceEuis?.includes(receivedObject.initialRequest?.device?.eui ?? 'never'));
            return entity;
        };
        const isDCTMatchedByDevice = (receivedObject) => {
            const deviceZombie = getZombieInstanceFromPrimaryKey(`${receivedObject.initialRequest?.device?.sk ?? 'never'}`);
            return getMatchedEntityViaZombieDevice(deviceZombie);
        };
        const isDeviceConfigMatchedByDeviceEui = (receivedObject) => {
            const stateEntities = nodeEdgeMapperState.nodeEdgeMapper.getContainedItems().filter(isDeviceEnabledType);
            const eui = receivedObject.pk.split(':').at(1) ?? 'never';
            const entity = stateEntities.find((item) => item.deviceEuis?.includes(eui));
            return entity;
        };
        const isDeviceConfigMatchedByDevice = (receivedObject) => {
            const deviceZombie = getZombieInstanceFromPrimaryKey(`${receivedObject.pk}`);
            return getMatchedEntityViaZombieDevice(deviceZombie);
        };
        if (contextUser) {
            const dctTopic = config.aws.mqtt.deviceControlTransactionChannel.replace('[deviceSk]', device ? device.sk : '#');
            debug && console.log(`${canaryString} subscribing to ${dctTopic}`);
            dctChannelSubRef.current = subscribeToMQTTChannel(contextUser, dctTopic, (data) => {
                const receivedObject = data.value;
                debug && console.log(`${canaryString} Got message on DCT channel`, receivedObject);
                if (isValidDCT(receivedObject)) {
                    const entity = isDCTMatchedByDevice(receivedObject) || isDCTMatchedByDeviceEui(receivedObject);
                    if (entity) {
                        debug &&
                            console.log(`${canaryString} useDeviceControlChannelActions resolved from ${nodeEdgeMapperState.nemHash} by`, {
                                device: !!isDCTMatchedByDevice(receivedObject),
                                entity: !!isDCTMatchedByDeviceEui(receivedObject),
                            });
                        const action = { type: 'AddTransactions', value: { entity, dcts: [receivedObject] } };
                        dispatch(action);
                    }
                    else {
                        debug &&
                            console.log(`${canaryString} useDeviceControlChannelActions could not resolve Device for DCT from NEM ${nodeEdgeMapperState.nemHash}`, receivedObject, nodeEdgeMapperState.nodeEdgeMapper.getSummary());
                    }
                }
            });
            if (loadDeviceConfigs) {
            }
            const deviceConfigTopic = config.aws.mqtt.deviceConfigUpdateChannel.replace('[deviceSk]', device ? device.sk : '#');
            debug && console.log(`${canaryString} subscribing to ${deviceConfigTopic}`);
            responseChannelSubRef.current = subscribeToMQTTChannel(contextUser, deviceConfigTopic, (data) => {
                const receivedObject = data.value;
                debug && console.log(`${canaryString} Got message on deviceConfigUpdateChannel`, receivedObject);
                if (isDeviceConfig(receivedObject)) {
                    const entity = isDeviceConfigMatchedByDevice(receivedObject) || isDeviceConfigMatchedByDeviceEui(receivedObject);
                    if (entity) {
                        debug &&
                            console.log(`${canaryString} useDeviceControlChannelActions resolved from ${nodeEdgeMapperState.nemHash} DeviceConfig by`, {
                                device: !!isDeviceConfigMatchedByDevice(receivedObject),
                                entity: !!isDeviceConfigMatchedByDeviceEui(receivedObject),
                            });
                        const action = { type: 'loadItems', value: { canaryString: 'useDeviceControlChannelActions (DeviceConfig)', items: [receivedObject] } };
                        nodeEdgeMapperDispatch(action);
                    }
                    else {
                        debug &&
                            console.log(`${canaryString} useDeviceControlChannelActions  could not resolve Device for DeviceConfg from NEM ${nodeEdgeMapperState.nemHash}`, receivedObject, nodeEdgeMapperState.nodeEdgeMapper.getSummary());
                    }
                }
                else {
                    console.log(`WARN: useDeviceControlChannelActions, not a DeviceConfig`, receivedObject);
                }
            });
        }
        else {
        }
        return () => {
            dctChannelSubRef.current?.unsubscribe();
            responseChannelSubRef.current?.unsubscribe();
        };
    }, [contextUser?.sk, device?.sk, nemHash]);
};
